import { getLensUriRouter } from "./lensUriRouter.js";
function recordOf(value) {
    return value !== null && typeof value === "object" ? value : null;
}
/** Map a host command into an engine action or URI-router operation. */
export async function dispatchEngineCommand(command, payload, actions, engine) {
    const fields = recordOf(payload);
    const source = (fields?.url ?? payload);
    const options = recordOf(fields?.options);
    const preferredFormat = recordOf(fields?.preferredFormat);
    const cameraConfig = {
        deviceId: typeof fields?.deviceId === "string" ? fields.deviceId : undefined,
        preferredFormat: preferredFormat
            && typeof preferredFormat.width === "number"
            && typeof preferredFormat.height === "number"
            && typeof preferredFormat.fps === "number"
            ? preferredFormat
            : undefined,
        orientation: fields?.orientation,
    };
    switch (command) {
        case "loadLens":
            return actions.loadLens(source, options ?? undefined);
        case "applyLens":
        case "loadLensUrl":
            return actions.applyLens(source, options ?? undefined);
        case "addLens":
        case "addLensUrl":
            return actions.addLens(source, options ?? undefined);
        case "removeLens":
            return actions.removeLens(typeof fields?.lensId === "string" ? fields.lensId : undefined);
        case "clearLenses":
        case "clearLens":
        case "unloadLens":
            return actions.clearLenses();
        case "startCamera":
            return actions.startCamera(cameraConfig);
        case "startCameraByLabel": {
            const devices = await navigator.mediaDevices?.enumerateDevices?.() ?? [];
            const needle = String(fields?.label ?? "").toLowerCase();
            const match = devices.find((device) => (device.kind === "videoinput"
                && (!needle || String(device.label || "").toLowerCase().includes(needle))));
            if (!match)
                throw new Error(`No camera label matched '${fields?.label ?? ""}'.`);
            return actions.startCamera({
                deviceId: match.deviceId,
                preferredFormat: cameraConfig.preferredFormat,
                orientation: cameraConfig.orientation,
            });
        }
        case "stopCamera":
            return actions.stopCamera();
        case "setOutputSize":
            return actions.setOutputSize(Number(fields?.width), Number(fields?.height));
        case "setFPSLimit":
            return actions.setFPSLimit(Number(fields?.fps));
        case "setDeviceClass":
        case "set-device-class":
            return actions.setDeviceClass(fields?.deviceClass);
        case "triggerEffect": {
            const raw = await engine?.ensureRaw?.();
            if (!raw?.call)
                throw new Error("Lens engine does not expose effect calls.");
            return raw.call(String(fields?.name || "triggerEffect"), recordOf(fields?.payload) ?? {});
        }
        case "lensUriSubscriptions":
        case "uriSubscriptions":
            if (!engine)
                throw new Error("Lens engine is not ready.");
            return getLensUriRouter(engine).listSubscriptions();
        case "lensUriHistory":
        case "uriHistory":
            if (!engine)
                throw new Error("Lens engine is not ready.");
            return getLensUriRouter(engine).history;
        case "sendLensUri":
        case "lensUriSend": {
            if (!engine)
                throw new Error("Lens engine is not ready.");
            const key = fields?.subscription ?? fields?.channel ?? fields?.key;
            const body = Object.prototype.hasOwnProperty.call(fields ?? {}, "data")
                ? fields?.data
                : fields?.body;
            return getLensUriRouter(engine).send(String(key ?? ""), body, {
                code: typeof fields?.code === "number" ? fields.code : undefined,
                description: typeof fields?.description === "string" ? fields.description : undefined,
                contentType: typeof fields?.contentType === "string" ? fields.contentType : "application/json",
                metadata: recordOf(fields?.metadata) ?? undefined,
            });
        }
        case "respondLensUri":
        case "lensUriRespond": {
            if (!engine)
                throw new Error("Lens engine is not ready.");
            if (fields?.identifier == null)
                throw new Error("respondLensUri requires an identifier.");
            const body = Object.prototype.hasOwnProperty.call(fields, "data") ? fields.data : fields.body;
            return getLensUriRouter(engine).respond(fields.identifier, body, {
                code: typeof fields.code === "number" ? fields.code : undefined,
                description: typeof fields.description === "string" ? fields.description : undefined,
                contentType: typeof fields.contentType === "string" ? fields.contentType : "application/json",
                metadata: recordOf(fields.metadata) ?? undefined,
            });
        }
        default:
            throw new Error(`unknown command: ${command}`);
    }
}
//# sourceMappingURL=engineCommandDispatcher.js.map